import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Code, Globe, Layout, Palette, Ruler, Sparkles, Type } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCreateAnalysis } from '@/hooks/useAnalyses';
import { usePreferencesStore } from '@/stores/preferences.store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ApiError } from '@/api/client';

const EXAMPLE_URL = 'https://stripe.com';

const CAPABILITIES = [
  {
    icon: Palette,
    title: 'Colors',
    body: 'Palette, semantic roles and WCAG contrast, weighted by painted area.',
  },
  {
    icon: Type,
    title: 'Typography',
    body: 'Families, a named type scale, weights and line heights.',
  },
  {
    icon: Ruler,
    title: 'Spacing',
    body: 'The recurring grid behind padding, margin and gap values.',
  },
  {
    icon: Layout,
    title: 'Layout',
    body: 'Container widths, grid patterns and responsive behaviour.',
  },
  {
    icon: Code,
    title: 'Components',
    body: 'Buttons, cards, inputs and navigation, with measured variants.',
  },
  {
    icon: Sparkles,
    title: 'design.md',
    body: 'One structured document an AI coding agent can build from.',
  },
];

/** Landing page. One job: get a URL and start an analysis. */
export function HomePage() {
  useDocumentTitle();
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const defaults = usePreferencesStore((state) => state.defaults);
  const createAnalysis = useCreateAnalysis();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!url.trim()) {
      setError('Enter a website address to analyze.');
      return;
    }
    try {
      const result = await createAnalysis.mutateAsync({ url: url.trim(), options: defaults });
      navigate(`/analyses/${result.analysis.id}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not start the analysis.');
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <section className="text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-fg sm:text-5xl">
          Turn any website into a reusable design system.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted sm:text-lg">
          Paste a URL and generate a structured <code className="font-mono text-fg">design.md</code>{' '}
          for AI-assisted development.
        </p>

        <form onSubmit={submit} className="mx-auto mt-10 max-w-2xl" noValidate>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex-1">
              <label htmlFor="home-url" className="sr-only">
                Website URL
              </label>
              <Input
                id="home-url"
                name="url"
                type="url"
                inputMode="url"
                autoComplete="url"
                inputSize="lg"
                placeholder="https://example.com"
                value={url}
                invalid={Boolean(error)}
                aria-describedby={error ? 'home-url-error' : 'home-url-hint'}
                onChange={(event) => {
                  setUrl(event.target.value);
                  if (error) setError(null);
                }}
                leadingIcon={<Globe className="h-4 w-4" />}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={createAnalysis.isPending}
              trailingIcon={<ArrowRight className="h-4 w-4" />}
              className="sm:w-auto"
            >
              Analyze
            </Button>
          </div>

          {error ? (
            <p id="home-url-error" role="alert" className="mt-3 text-sm text-danger">
              {error}
            </p>
          ) : (
            <p id="home-url-hint" className="mt-3 text-sm text-subtle">
              Extract colors, typography, spacing, components, layout patterns and visual style
              automatically.
            </p>
          )}

          <p className="mt-2 text-xs text-subtle">
            Try{' '}
            <button
              type="button"
              onClick={() => setUrl(EXAMPLE_URL)}
              className="font-mono text-accent underline-offset-2 hover:underline"
            >
              {EXAMPLE_URL}
            </button>
          </p>
        </form>
      </section>

      <section className="mt-20" aria-labelledby="capabilities-heading">
        <h2 id="capabilities-heading" className="sr-only">
          What gets extracted
        </h2>
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="bg-surface p-5">
                <Icon className="h-4 w-4 text-accent" aria-hidden="true" />
                <h3 className="mt-3 text-sm font-medium text-fg">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-subtle">{item.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-16 rounded-lg border border-border bg-surface/40 p-6 text-center">
        <p className="text-sm text-muted">
          Need more control over crawl depth, viewports and analysis settings?
        </p>
        <Link
          to="/analyses/new"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
        >
          Open the full analysis form
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </section>
    </div>
  );
}
